import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import debounce from "lodash/debounce";

// Ensure these paths match your project structure
import Spinner from "../components/Common/Spinner";
import Pagination from "../components/Pagination";

// Import your API function here
import { getContactus } from "../../services/Admin/adminServices";

const ContactUs = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // Eye Icon SVG for the Action column
  const eyeSvg = (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#87C55F" viewBox="0 0 16 16" style={{ cursor: "pointer" }}>
      <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z" />
      <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z" />
    </svg>
  );

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await getContactus(currentPage, itemsPerPage, debouncedSearch);

      // Adjust this path based on your exact API response structure
      setContacts(response?.data?.data?.contactUss || []);
      setTotalItems(response?.data?.data?.total || 0);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load contact messages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearch]);

  useEffect(() => {
    setCurrentPage(0);
    const handler = debounce(() => {
      setDebouncedSearch(search);
    }, 500);

    handler();

    return () => {
      handler.cancel();
    };
  }, [search]);

  const openViewModal = (contact) => {
    setSelectedContact(contact);
    setShowModal(true);
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 style={{ color: "#87C55F" }}>Contact Us </h2>
      </div>

      <div className="pb-2 d-flex flex-wrap justify-content-between gap-2 align-items-end mb-4">
        <div className="flex-grow-1">
          <input
            type="text"
            style={{ borderRadius: "0.5rem", borderColor: "#87C55F" }}
            className="form-control w-100"
            placeholder="Search by name or email....."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {search && (
          <div>
            <button
              type="button"
              className="btn btn-danger text-white"
              style={{ borderRadius: "0.5rem" }}
              onClick={() => {
                setSearch("");
              }}
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <Spinner />
      ) : (
        <table className="table table-striped align-middle shadow-sm">
          <thead className="table-light">
            <tr>
              <th>Sr No.</th>
              <th>Name</th>
              <th>Email</th>
              <th>Date</th>
              <th style={{ textAlign: "center" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {contacts?.length > 0 ? (
              contacts.map((contact, index) => (
                <tr key={contact._id || index}>
                  <td>{currentPage * itemsPerPage + index + 1}</td>
                  <td>{contact?.name || "N/A"}</td>
                  <td>{contact?.email || "N/A"}</td>
                  <td>
                    {contact?.createdAt
                      ? new Date(contact?.createdAt).toLocaleString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true, // for AM/PM
                      })
                      : "N/A"}

                  </td>
                  <td style={{ textAlign: "center" }}>
                    <div
                      onClick={() => openViewModal(contact)}
                      title="View Message"
                      style={{ display: "inline-block", padding: "5px" }}
                    >
                      {eyeSvg}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center">
                  No contact messages found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {contacts?.length > 0 && (
        <div className="card-footer clearfix">
          <div className="d-flex justify-content-between align-items-center">
            <div className="dataTables_info" style={{ color: "#87C55F" }}>
              Showing {currentPage * itemsPerPage + 1} to{" "}
              {totalItems > (currentPage + 1) * itemsPerPage
                ? (currentPage + 1) * itemsPerPage
                : totalItems}{" "}
              of {totalItems} entries
            </div>
            <div className="d-flex align-items-center" style={{ gap: "10px" }}>
              <Pagination
                pageCount={totalPages}
                pageValue={currentPage}
                setPage={setCurrentPage}
              />
            </div>
          </div>
        </div>
      )}

      {/* View Message Modal */}
      <Modal size="lg" show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ color: "#87C55F" }}>
            Message Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedContact ? (
            <div>
              <div className="mb-3">
                <h6 className="text-muted mb-1">From</h6>
                <p className="mb-0 fw-bold">{selectedContact.name} &lt;{selectedContact.email}&gt;</p>
              </div>
              <div className="mb-3">
                <h6 className="text-muted mb-1">Subject</h6>
                <p className="p-3   border rounded shadow-lg">
                  {selectedContact.subject || "No Subject Provided"}
                </p>
              </div>
              <div className="mb-3">
                <h6 className="text-muted mb-1">Message</h6>
                <div
                  className="p-3  border rounded shadow-lg"
                  style={{ whiteSpace: "pre-wrap", minHeight: "100px" }}
                >
                  {selectedContact.message || "No Message Provided"}
                </div>
              </div>
            </div>
          ) : (
            <p>Loading details...</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => setShowModal(false)}
            style={{ backgroundColor: "#87C55F", border: "none" }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ContactUs; 